import React, { useState, useEffect } from 'react';
import { signOut, useSession } from '../../lib/auth';
import AdminRequests from './AdminRequests';
import AdminNotifications from './AdminNotifications';
import AdminGolfCourses from './AdminGolfCourses';
import { 
  BarChart3, 
  Users, 
  Bell, 
  LogOut, 
  Activity,
  TrendingUp,
  Clock,
  Mail,
  MapPin
} from 'lucide-react';

interface DashboardStats {
  totalRequests: number;
  activeRequests: number;
  totalNotifications: number;
}

interface DashboardData {
  stats: DashboardStats;
  recentRequests: any[];
  recentNotifications: any[];
}

function AdminDashboard() {
  const { data: session } = useSession();
  const [activeTab, setActiveTab] = useState('dashboard');
  const [dashboardData, setDashboardData] = useState<DashboardData | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:3000';
      
      const response = await fetch(`${apiUrl}/api/admin/dashboard`, {
        credentials: 'include',
      });
      
      if (response.ok) {
        const result = await response.json();
        setDashboardData(result.data);
      }
    } catch (error) {
      console.error('Failed to fetch dashboard data:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleLogout = async () => {
    await signOut();
  };

  const tabs = [
    { id: 'dashboard', label: 'Dashboard', icon: BarChart3 },
    { id: 'requests', label: 'Requests', icon: Users },
    { id: 'notifications', label: 'Notifications', icon: Bell },
    { id: 'golf-courses', label: 'Golf Courses', icon: MapPin },
  ];

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Header */}
      <header className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center space-x-4">
              <div className="flex items-center space-x-3">
                <img 
                  src="/logos/admin/logo.svg" 
                  alt="Snag Your Tee Admin" 
                  className="w-10 h-10 object-contain drop-shadow-sm"
                />
                <h1 className="text-xl font-semibold text-gray-900">
                  Snag Your Tee Admin
                </h1>
              </div>
            </div>
            
            <div className="flex items-center space-x-4">
              <span className="text-sm text-gray-600">
                Welcome, {session?.user?.email}
              </span>
              <button
                onClick={handleLogout}
                className="flex items-center space-x-2 text-gray-600 hover:text-gray-800 transition-colors"
              >
                <LogOut size={18} />
                <span>Logout</span>
              </button>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Navigation Tabs */}
        <div className="border-b border-gray-200 mb-8">
          <nav className="-mb-px flex space-x-8">
            {tabs.map((tab) => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center space-x-2 py-2 px-1 border-b-2 font-medium text-sm transition-colors ${
                    activeTab === tab.id
                      ? 'border-indigo-500 text-indigo-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  <Icon size={18} />
                  <span>{tab.label}</span>
                </button>
              );
            })}
          </nav>
        </div>

        {/* Dashboard Content */}
        {activeTab === 'dashboard' && (
          <div className="space-y-8">
            {/* Stats Grid */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="bg-white rounded-lg shadow p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-gray-600">Total Requests</p>
                    <p className="text-3xl font-bold text-gray-900">
                      {dashboardData?.stats.totalRequests || 0}
                    </p>
                  </div>
                  <div className="p-3 bg-blue-100 rounded-full">
                    <Users className="text-blue-600" size={24} />
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-lg shadow p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-gray-600">Active Monitors</p>
                    <p className="text-3xl font-bold text-green-600">
                      {dashboardData?.stats.activeRequests || 0}
                    </p>
                  </div>
                  <div className="p-3 bg-green-100 rounded-full">
                    <Activity className="text-green-600" size={24} />
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-lg shadow p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-gray-600">Notifications Sent</p>
                    <p className="text-3xl font-bold text-purple-600">
                      {dashboardData?.stats.totalNotifications || 0}
                    </p>
                  </div>
                  <div className="p-3 bg-purple-100 rounded-full">
                    <Mail className="text-purple-600" size={24} />
                  </div>
                </div>
              </div>
            </div>

            {/* Recent Activity */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Recent Requests */}
              <div className="bg-white rounded-lg shadow">
                <div className="p-6 border-b border-gray-200">
                  <h3 className="text-lg font-medium text-gray-900 flex items-center space-x-2">
                    <Clock size={20} />
                    <span>Recent Requests</span>
                  </h3>
                </div>
                <div className="p-6">
                  {dashboardData?.recentRequests?.length ? (
                    <div className="space-y-4">
                      {dashboardData.recentRequests.slice(0, 5).map((request, index) => (
                        <div key={index} className="flex items-center justify-between py-2">
                          <div>
                            <p className="text-sm text-gray-600">
                              {new Date(request.created_at).toLocaleDateString()}
                            </p>
                          </div>
                          <div className="text-sm text-gray-500">
                            Request #{index + 1}
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-gray-500 text-center py-4">No recent requests</p>
                  )}
                </div>
              </div>

              {/* Recent Notifications */}
              <div className="bg-white rounded-lg shadow">
                <div className="p-6 border-b border-gray-200">
                  <h3 className="text-lg font-medium text-gray-900 flex items-center space-x-2">
                    <TrendingUp size={20} />
                    <span>Recent Notifications</span>
                  </h3>
                </div>
                <div className="p-6">
                  {dashboardData?.recentNotifications?.length ? (
                    <div className="space-y-4">
                      {dashboardData.recentNotifications.slice(0, 5).map((notification, index) => (
                        <div key={index} className="flex items-center justify-between py-2">
                          <div>
                            <p className="text-sm font-medium text-gray-900">
                              {notification.notification_method} notification
                            </p>
                            <p className="text-sm text-gray-600">
                              {new Date(notification.notification_sent_at).toLocaleDateString()}
                            </p>
                          </div>
                          <div className={`px-2 py-1 rounded-full text-xs ${
                            notification.is_successful 
                              ? 'bg-green-100 text-green-800' 
                              : 'bg-red-100 text-red-800'
                          }`}>
                            {notification.is_successful ? 'Success' : 'Failed'}
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-gray-500 text-center py-4">No recent notifications</p>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Requests Tab */}
        {activeTab === 'requests' && <AdminRequests />}

        {/* Notifications Tab */}
        {activeTab === 'notifications' && <AdminNotifications />}

        {/* Golf Courses Tab */}
        {activeTab === 'golf-courses' && <AdminGolfCourses />}
      </div>
    </div>
  );
}

export default AdminDashboard;